import classNames from 'classnames/bind';
import { HTMLAttributes, RefObject, useEffect, useRef, useState } from 'react';

import { Exit, Search } from '../../assets/icons/Icons';
import useClickOutside from '../../hooks/useClickOutside/useClickOutside';
import Styles from './searchBox.module.scss';

const cx = classNames.bind(Styles);

export interface Props extends Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** What the page should do when search query is entered */
  onChange?: (value: string) => void;
  /** optional function to run on enter keystroke */
  onSubmit?: (value: unknown) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Enables full width searchbox */
  fullWidth?: boolean;
  /** Disables the search bar */
  disabled?: boolean;
  /** Focuses on the element after rendering the search bar */
  focusOnRender?: boolean;
  /** Value for the search query */
  value?: string;
  /** Default value for the search query */
  defaultValue?: string;
  /** ID prop applied to input and label to associate them together for screen readers. */
  id: string;
  /** Label text to describe associated input field. */
  label?: string;
  /** Determines whether or not associated Label is visible. */
  showLabel?: boolean;
  /** This should be set to where the user is searching (i.e. Sitewide, On this page) */
  'aria-label'?: string;
  /** Shows a loading spinner inside the search box */
  loading?: boolean;
}

/* Accessibility Notes:
  https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/search_role

  aria-label - This should be defined where the user is searching (i.e. Sitewide, On this page).
*/
const SearchBox = ({
  placeholder = 'Search for...',
  fullWidth = false,
  disabled = false,
  defaultValue = '',
  id,
  label = 'Label',
  showLabel = false,
  focusOnRender,
  value,
  loading = false,
  onChange = () => {
    return;
  },
  onSubmit,
  ...otherProps
}: Props) => {
  const [searchValue, setSearchValue] = useState(defaultValue || '');
  const [doneSearching, setDoneSearching] = useState(true);
  const searchInput = useRef<HTMLInputElement>(null); // to handle clear and focus events

  if (!otherProps['aria-label']) {
    otherProps['aria-label'] = 'On this page';
  }

  const focus = () => {
    searchInput.current?.focus({ preventScroll: true });
    setDoneSearching(false);
  };

  useEffect(() => {
    if (focusOnRender) {
      focus();
    }
  }, [focusOnRender]);

  const onSearchChanged = (value) => {
    setSearchValue(value);
    submit(value);
    setDoneSearching(false);
  };

  const submit = (value) => {
    if (onChange) {
      onChange(value);
    }
  };

  const clear = () => {
    onSearchChanged('');
    clearSearch();
    focus();
    setDoneSearching(false);
  };

  const clearSearch = () => {
    onSearchChanged('');
  };

  const ref = useClickOutside(() => setDoneSearching(true), !doneSearching) as RefObject<HTMLDivElement>;
  const removeIcon = doneSearching && searchValue;
  const showSearchIcon = !removeIcon && !loading;
  const showSpinner = loading && !removeIcon;

  return (
    <div
      className={cx('form-element', 'wrapper', { 'wrapper-fullwidth': fullWidth, fw: fullWidth })}
      data-testid='searchbox'>
      {showLabel && (
        <label htmlFor={id} className={cx({ label: showLabel, 'label--disabled': showLabel && disabled })}>
          {label}
        </label>
      )}
      <div className={cx({ 'searchbox-disabled': disabled })}>
        <div className={cx('searchbox')} ref={ref}>
          <span className={cx('searchbox-search-icon')}>
            {showSearchIcon && Search}
            {showSpinner && <i className='fa-solid fa-spinner fa-spin'></i>}
          </span>
          <input
            type='search'
            autoComplete='off'
            ref={searchInput}
            placeholder={placeholder}
            className={cx('searchbox-input-field', { 'searchbox-input-field__iconRemoved': removeIcon })}
            onChange={(e) => {
              if (!disabled) {
                onSearchChanged(e.target.value);
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                if (onSubmit) {
                  onSubmit(searchValue);
                }
                (e.target as HTMLElement).blur();
              }
            }}
            disabled={disabled}
            value={value || searchValue}
            {...otherProps}
          />
          {searchValue && (
            <button className={cx('searchbox__btn-clear')} onClick={() => clear()} data-testid='clear-button'>
              {Exit}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchBox;
